import { useTranslation } from '@/i18n'
import type { Interview } from '@/service/api'

interface Props {
  interview: Interview
}

const Placeholder = ({ text }: { text: string }) => (
  <p className="text-sm text-muted-foreground italic">{text}</p>
)

const InterviewGuideTab = ({ interview }: Props) => {
  const { t } = useTranslation()
  const questions = interview.questions ?? []

  return (
    <div className="flex flex-col gap-8 max-w-[760px]">
      {/* Interview Topics — backend has no dedicated long-form field; show the topic title + placeholder */}
      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-foreground">
          {t('hek_topic:guide.topics.title', { defaultValue: 'Interview Topics' })}
        </h2>
        {interview.topic ? (
          <p className="text-sm text-foreground leading-relaxed">{interview.topic}</p>
        ) : (
          <Placeholder
            text={t('hek_topic:guide.topics.placeholder', {
              defaultValue: 'No interview topic specified yet.',
            })}
          />
        )}
      </section>

      {/* Interview Goals — no backend field yet */}
      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-foreground">
          {t('hek_topic:guide.goals.title', { defaultValue: 'Interview Goals:' })}
        </h2>
        <Placeholder
          text={t('hek_topic:guide.goals.placeholder', {
            defaultValue: 'Interview goals will appear here once the brief is finalized.',
          })}
        />
      </section>

      {/* Interview Questions — backed by Interview.questions */}
      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-foreground">
          {t('hek_topic:guide.questions.title', { defaultValue: 'Interview Questions:' })}
        </h2>
        {questions.length === 0 ? (
          <Placeholder
            text={t('hek_topic:guide.questions.empty', {
              defaultValue: 'No interview questions defined yet.',
            })}
          />
        ) : (
          <ol className="flex flex-col gap-1.5 list-none">
            {questions.map((q, i) => (
              <li key={i} className="text-sm text-foreground leading-relaxed">
                <span className="font-medium">Q{i + 1}:</span> {q}
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  )
}

export default InterviewGuideTab
